import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Scale, FileText, TrendingUp, LogOut, Lock, Calendar, 
  BarChart3, Shield, Users, Settings, ChevronDown, ChevronRight, Briefcase,
  User, FileSearch, ClipboardCheck, Building2
} from 'lucide-react';
import axios from 'axios';
import { useBranding } from '../lib/BrandingContext';
import ModuleWalletDropdown from '../components/ModuleWalletDropdown';
import BuyCreditsModal from '../components/BuyCreditsModal';
import AdvocateQueue from '../components/AdvocateQueue';
import CanonicalMigrationStatus from '../components/admin/CanonicalMigrationStatus';

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const { branding } = useBranding();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  const [featureToggles, setFeatureToggles] = useState({
    create_legal_opinion: true,
    ec_extractor: true,
    institution_checklist: true,
    missing_doc_identifier: true,
    cs_dashboard: true,
    legal_opinion_validator: true
  });
  const [expandedSections, setExpandedSections] = useState({
    analytics: false,
    applications: false,
    admin: false,
    settings: false,
    security: false,
    actions: false
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      navigate('/login');
      return;
    }

    setUser(JSON.parse(userData));
    fetchStats();
    fetchFeatureToggles();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/stats`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchFeatureToggles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/feature-toggles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Handle both formats - direct toggles or {toggles: {...}} wrapper
      const toggles = response.data.toggles || response.data;
      setFeatureToggles(toggles);
    } catch (err) {
      console.error('Error fetching feature toggles:', err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {branding?.logo_url ? (
                <img src={branding.logo_url} alt={branding.company_name || 'Logo'} className="h-10" />
              ) : (
                <div className="flex items-center space-x-2">
                  <Scale className="w-8 h-8 text-blue-600" />
                  <span className="text-xl font-bold text-gray-900">
                    {branding?.company_name || 'TechOrange-Legal'}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.full_name}</p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
                {user?.role && (
                  <p className="text-xs text-blue-600 font-medium capitalize">{user.role.replace('_', ' ')}</p>
                )}
              </div>
              
              <ModuleWalletDropdown />
              
              <button
                onClick={() => navigate('/password-management')}
                className="flex items-center space-x-2 px-4 py-2 text-sm bg-gray-600 text-white hover:bg-gray-700 rounded-lg transition-colors"
                title="Password Management"
              >
                <Lock className="w-4 h-4" />
                <span>Password</span>
              </button>
              
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Left Sidebar */}
        <aside className="w-64 bg-gray-100 border-r border-gray-200 min-h-screen overflow-y-auto">
          <nav className="p-4 space-y-2">
            {/* Home/Analytics Section */}
            <div>
              <button
                onClick={() => toggleSection('analytics')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <span>Home / Analytics</span>
                {expandedSections.analytics ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.analytics && (
              <div className="ml-4 mt-2 space-y-1">
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  Total documents
                </div>
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  Total Opinions Generated
                </div>
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  Total Extractions
                </div>
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  Total Bank Format Distribution
                </div>
                <div className="px-4 py-2 text-sm text-gray-600 cursor-default">
                  Recent Documents
                </div>
              </div>
              )}
            </div>

            {/* Applications Section */}
            <div>
              <button
                onClick={() => toggleSection('applications')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <span>Applications</span>
                {expandedSections.applications ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
              )}
              </button>
              {expandedSections.applications && (
              <div className="ml-4 mt-2 space-y-1">
                {featureToggles.create_legal_opinion && (
                  <button
                    onClick={() => navigate('/extract')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <Scale className="w-4 h-4 text-blue-500" />
                    <span>Create Legal Opinion</span>
                  </button>
                )}
                {featureToggles.ec_extractor && (
                  <button
                    onClick={() => navigate('/ec-extractor')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <FileSearch className="w-4 h-4 text-green-500" />
                    <span>EC Extractor</span>
                  </button>
                )}
                {featureToggles.institution_checklist && (
                  <button
                    onClick={() => navigate('/institution-checklist')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded flex items-center space-x-2"
                  >
                    <ClipboardCheck className="w-4 h-4 text-amber-500" />
                    <span>Institutional Checklist</span>
                  </button>
                )}
                {featureToggles.missing_doc_identifier && (
                  <button
                    onClick={() => navigate('/missing-docs')}
                    className="w-full text-left px-4 py-2 text-sm text-amber-600 hover:bg-amber-50 rounded font-medium"
                  >
                    📋 Missing Doc Identifier
                  </button>
                )}
                {featureToggles.cs_dashboard && (
                  <div className="space-y-0">
                    <div className="px-4 py-2 text-sm text-indigo-600 font-medium flex items-center">
                      <span className="mr-2">🏢</span> CS Dashboard
                    </div>
                    <div className="ml-4 border-l-2 border-indigo-200 pl-2">
                      <button
                        onClick={() => navigate('/cs-report')}
                        className="w-full text-left px-3 py-1.5 text-sm text-indigo-500 hover:bg-indigo-50 rounded flex items-center"
                        data-testid="cs-report-nav"
                      >
                        <span className="mr-2">📋</span> CS Compliance Opinion Generator
                      </button>
                    </div>
                  </div>
                )}
                {featureToggles.legal_opinion_validator && (
                  <button
                    onClick={() => navigate('/legal-opinion-validation')}
                    className="w-full text-left px-4 py-2 text-sm text-emerald-600 hover:bg-emerald-50 rounded font-medium"
                    data-testid="love-dashboard-nav"
                  >
                    ⚖️ Legal Opinion Validator
                  </button>
                )}
              </div>
              )}
            </div>

            {/* Admin Dashboard Section */}
            <div>
              <button
                onClick={() => toggleSection('admin')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <span>Admin Dashboard</span>
                {expandedSections.admin ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.admin && (
              <div className="ml-4 mt-2 space-y-1">
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  All Existing Functionalities
                </button>
                <button
                  onClick={() => navigate('/chats')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
                >
                  💬 Messages
                </button>
                <button
                  onClick={() => navigate('/audit-logs')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium"
                >
                  📋 Audit Logs
                </button>
              </div>
              )}
            </div>

            {/* Settings Section */}
            <div>
              <button
                onClick={() => toggleSection('settings')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <span className="flex items-center space-x-2">
                  <Settings className="w-4 h-4" />
                  <span>Settings</span>
                </span>
                {expandedSections.settings ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.settings && (
              <div className="ml-4 mt-2 space-y-1">
                <button
                  onClick={() => navigate('/admin/company-settings')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium flex items-center space-x-2"
                >
                  <Building2 className="w-4 h-4" />
                  <span>Company Master (GST)</span>
                </button>
                <button
                  onClick={() => navigate('/settings/advocate-profile')}
                  className="w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded font-medium flex items-center space-x-2"
                >
                  <User className="w-4 h-4" />
                  <span>Advocate Profile</span>
                </button>
              </div>
              )}
            </div>

            {/* Security Section */}
            <div>
              <button
                onClick={() => toggleSection('security')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <span>Security</span>
                {expandedSections.security ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.security && (
              <div className="ml-4 mt-2 space-y-1">
                <button
                  onClick={() => navigate('/password-management')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  Passwords
                </button>
              </div>
              )}
            </div>

            {/* Actions Section */}
            <div>
              <button
                onClick={() => toggleSection('actions')}
                className="w-full flex items-center justify-between px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors font-semibold"
              >
                <span>Actions</span>
                {expandedSections.actions ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {expandedSections.actions && (
              <div className="ml-4 mt-2 space-y-1">
                <button
                  onClick={() => navigate('/review')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  Review
                </button>
                <button
                  onClick={() => navigate('/my-opinions')}
                  className="w-full text-left px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded"
                >
                  View My Opinions
                </button>
              </div>
              )}
            </div>
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 p-8">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">System Overview</h2>

            {/* Canonical Layer Migration Status — live progress bar */}
            <CanonicalMigrationStatus />

            {/* Stats Grid - 3 columns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              {/* Total Documents */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Total Documents</h3>
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_documents || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">All uploaded documents</p>
                </div>
              </div>

              {/* Total Opinions */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Total Opinions</h3>
                  <Scale className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_opinions || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Legal opinions generated</p>
                </div>
              </div>

              {/* Total Extractions */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Total Extractions</h3>
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-3xl font-bold text-gray-900">{stats?.total_extractions || 0}</p>
                  <p className="text-xs text-gray-500 mt-1">Data extractions performed</p>
                </div>
              </div>
            </div>

            {/* Bank Format Distribution */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Bank Format Distribution</h3>
                <BarChart3 className="w-5 h-5 text-blue-600" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-gray-600">ICICI Bank</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.bank_formats?.icici || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">HDFC Bank</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.bank_formats?.hdfc || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">SBI Bank</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.bank_formats?.sbi || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Other Banks</p>
                  <p className="text-2xl font-bold text-gray-900">{stats?.bank_formats?.others || 0}</p>
                </div>
              </div>
            </div>

            {/* Recent Documents */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-700">Recent Documents</h3>
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div className="space-y-3">
                {stats?.recent_documents && stats.recent_documents.length > 0 ? (
                  stats.recent_documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-4 h-4 text-gray-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{doc.name || 'Untitled Document'}</p>
                          <p className="text-xs text-gray-500">{doc.type || 'Legal Opinion'} • {doc.date || 'Today'}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => navigate(`/extract/${doc.id}`)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        View
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500 text-center py-4">No recent documents</p>
                )}
              </div>
            </div>

            {/* Advocate Queue - Cases Ready for Opinion */}
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 mb-8" data-testid="advocate-queue-section">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-amber-600" />
                  📋 Cases Ready for Opinion
                </h3>
                <button
                  onClick={() => navigate('/missing-docs')}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + New Case
                </button>
              </div>
              <AdvocateQueue onOpenCase={(caseId) => {
                alert(`Case ${caseId} opened. You can now proceed with opinion generation.`);
              }} />
            </div>

            {/* Quick Admin Actions */}
            <div className="mt-8">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Admin Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <button
                  onClick={() => navigate('/audit-logs')}
                  className="bg-white hover:bg-indigo-50 border-2 border-indigo-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-indigo-100 p-2 rounded-lg">
                      <FileText className="w-6 h-6 text-indigo-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">📋 Audit Logs</h4>
                  </div>
                  <p className="text-sm text-gray-600">View system activity & user actions</p>
                </button>

                <button
                  onClick={() => navigate('/admin')}
                  className="bg-white hover:bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-blue-100 p-2 rounded-lg">
                      <Shield className="w-6 h-6 text-blue-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Admin Panel</h4>
                  </div>
                  <p className="text-sm text-gray-600">Manage users, tenants & settings</p>
                </button>

                <button
                  onClick={() => navigate('/admin/billing')}
                  className="bg-white hover:bg-green-50 border-2 border-green-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-green-100 p-2 rounded-lg">
                      <BarChart3 className="w-6 h-6 text-green-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Billing & Credits</h4>
                  </div>
                  <p className="text-sm text-gray-600">Manage tenant wallets & pricing</p>
                </button>

                <button
                  onClick={() => navigate('/admin/users')}
                  className="bg-white hover:bg-purple-50 border-2 border-purple-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-purple-100 p-2 rounded-lg">
                      <Users className="w-6 h-6 text-purple-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">User Management</h4>
                  </div>
                  <p className="text-sm text-gray-600">View and manage all users</p>
                </button>

                <button
                  onClick={() => navigate('/admin/company-settings')}
                  className="bg-white hover:bg-orange-50 border-2 border-orange-200 rounded-xl p-6 text-left transition-all hover:shadow-md"
                >
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="bg-orange-100 p-2 rounded-lg">
                      <Building2 className="w-6 h-6 text-orange-600" />
                    </div>
                    <h4 className="font-semibold text-gray-900">Company Master</h4>
                  </div>
                  <p className="text-sm text-gray-600">GST & Invoice settings</p>
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* Buy Credits Modal */}
      <BuyCreditsModal open={showBuyCredits} onClose={() => setShowBuyCredits(false)} />
    </div>
  );
};

export default SuperAdminDashboard;
